import {
  buildListings,
  type ColumnMap,
  DEFAULT_POLICY_IDS,
  DEFAULT_RULES,
  type EbayTemplate,
  type Flag,
  findHeaderRow,
  guessColumns,
  type ImportRules,
  mergeSupplement,
  parseCsv,
  parseEbayTemplate,
  readItems,
  toEbayCsv,
} from "@shared/listing-import";
import { readXlsx, type Sheet } from "@shared/xlsx-read";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "../lib/api";

/**
 * Excel import (Isaac, 2026-10-13): a stock spreadsheet in, an eBay Seller Hub
 * upload file out. Everything runs in the browser -- the spreadsheets are never
 * sent to the server. Rules live in shared/listing-import.ts. First use: the
 * Dremel stock sheet from the Tijuana warehouse.
 */

interface VerifyResult {
  ack: string;
  errors: { severity: "Error" | "Warning"; code: string; message: string }[];
  fees: Record<string, number>;
}

interface PublishResult {
  ack: string;
  errors: { severity: "Error" | "Warning"; code: string; message: string }[];
  itemId: string | null;
}

const FIELD_KEYS: (keyof ColumnMap)[] = [
  "sku",
  "title",
  "qty",
  "model",
  "price",
  "retail",
  "upc",
  "epid",
  "photo",
];
const BLOCKING: Flag[] = ["missingPrice", "missingPhoto"];

async function readAny(file: File): Promise<Sheet[]> {
  if (/\.csv$/i.test(file.name)) return [{ name: file.name, rows: parseCsv(await file.text()) }];
  return readXlsx(await file.arrayBuffer());
}

function download(name: string, text: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function ListingImport() {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [stock, setStock] = useState<{ name: string; sheets: Sheet[] } | null>(null);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [map, setMap] = useState<ColumnMap | null>(null);
  const [template, setTemplate] = useState<EbayTemplate | null>(null);
  const [supplement, setSupplement] = useState<{ name: string; rows: string[][] } | null>(null);
  const [rules, setRules] = useState<ImportRules>(DEFAULT_RULES);
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [categoryOverride, setCategoryOverride] = useState<Record<string, string>>({});
  const [location, setLocation] = useState("San Diego, CA");
  const [verified, setVerified] = useState<Record<string, VerifyResult>>({});
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [published, setPublished] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<"first" | "rest" | null>(null);
  const [republish, setRepublish] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const sheet = stock?.sheets[sheetIdx];
  const headerRow = useMemo(() => (sheet ? findHeaderRow(sheet.rows) : 0), [sheet]);
  const headers = sheet?.rows[headerRow] ?? [];

  const onStock = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const sheets = (await readAny(file)).filter((s) => s.rows.some((r) => r.length > 0));
      setStock({ name: file.name, sheets });
      setSheetIdx(0);
      const first = sheets[0];
      if (first) setMap(guessColumns(first.rows[findHeaderRow(first.rows)] ?? []));
      setCategoryOverride({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const onTemplate = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const tpl = parseEbayTemplate(await readXlsx(await file.arrayBuffer()));
      setTemplate(tpl);
      // Policy names must match the template's list exactly; pick the first match for each default.
      setRules((r) => ({
        ...r,
        shippingProfile: tpl.policies.shipping.includes(r.shippingProfile)
          ? r.shippingProfile
          : (tpl.policies.shipping[0] ?? r.shippingProfile),
        returnProfile: tpl.policies.returns.includes(r.returnProfile)
          ? r.returnProfile
          : (tpl.policies.returns[0] ?? r.returnProfile),
        paymentProfile: tpl.policies.payment.includes(r.paymentProfile)
          ? r.paymentProfile
          : (tpl.policies.payment[0] ?? r.paymentProfile),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  const onSupplement = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      const sheets = await readAny(file);
      setSupplement({ name: file.name, rows: sheets[0]?.rows ?? [] });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Researched retail prices / catalog IDs kept in the app, matched by SKU.
  const saved = useQuery({
    queryKey: ["listing-supplements"],
    queryFn: () =>
      api<{ sku: string; retail: number | null; epid: string | null; photo: string | null }[]>(
        "/api/v1/listings/supplements",
      ),
  });
  const savedRows = useMemo(
    () => [
      ["SKU", "Retail", "ePID", "Photo"],
      ...(saved.data ?? []).map((r) => [
        r.sku,
        r.retail?.toString() ?? "",
        r.epid ?? "",
        r.photo ?? "",
      ]),
    ],
    [saved.data],
  );

  const merged = useMemo(() => {
    if (!sheet || !map) return null;
    let items = readItems(sheet.rows, headerRow, map);
    let matched = 0;
    let unmatched = 0;
    // Saved research first, then an uploaded file on top (it wins).
    for (const rows of [savedRows, supplement?.rows]) {
      if (!rows || rows.length < 2) continue;
      const m = mergeSupplement(items, rows);
      items = m.items;
      matched = Math.max(matched, m.matched);
      if (rows === supplement?.rows) unmatched = m.unmatched;
    }
    return { items, matched, unmatched };
  }, [sheet, map, headerRow, supplement, savedRows]);

  const listings = useMemo(() => {
    if (!merged) return [];
    return buildListings(merged.items, rules).map((l) => {
      const to = categoryOverride[l.category.id];
      return to ? { ...l, category: { id: to, name: to } } : l;
    });
  }, [merged, rules, categoryOverride]);

  const flagCounts = useMemo(() => {
    const c: Partial<Record<Flag, number>> = {};
    for (const l of listings) for (const f of l.flags) c[f] = (c[f] ?? 0) + 1;
    return c;
  }, [listings]);
  const categories = useMemo(() => {
    const c = new Map<string, { name: string; count: number }>();
    for (const l of listings) {
      const k = l.category.id;
      c.set(k, { name: l.category.name, count: (c.get(k)?.count ?? 0) + 1 });
    }
    return [...c.entries()];
  }, [listings]);

  const ready = listings.filter((l) => !l.blocked);
  const rejectedByEbay = (sku: string) => {
    const v = verified[sku];
    return !!v && v.ack !== "Success" && v.ack !== "Warning";
  };
  const needAttention = (l: (typeof listings)[number]) =>
    l.blocked ||
    l.flags.some((f) => f !== "titleCutOff") || // "Title rewritten" is informational
    rejectedByEbay(l.sku);
  const shown = onlyAttention ? listings.filter(needAttention) : listings;
  const missingBlocking = ready.filter((l) => l.flags.some((f) => BLOCKING.includes(f))).length;

  const policyWarn = (name: string, list: string[] | undefined) =>
    template && list && list.length > 0 && !list.includes(name);

  const ids = template?.policyIds ?? DEFAULT_POLICY_IDS;
  const profileIds =
    (ids.shipping as Record<string, string>)[rules.shippingProfile] &&
    (ids.returns as Record<string, string>)[rules.returnProfile] &&
    (ids.payment as Record<string, string>)[rules.paymentProfile]
      ? {
          shippingProfileId: (ids.shipping as Record<string, string>)[
            rules.shippingProfile
          ] as string,
          returnProfileId: (ids.returns as Record<string, string>)[rules.returnProfile] as string,
          paymentProfileId: (ids.payment as Record<string, string>)[rules.paymentProfile] as string,
        }
      : null;
  const checkable = ready.filter((l) => l.price !== null && l.qty >= 1);

  const verify = useMutation({
    mutationFn: async () => {
      if (!profileIds) throw new Error(t("import:needPolicyIds"));
      setVerifyError(null);
      const results: Record<string, VerifyResult> = {};
      for (let i = 0; i < checkable.length; i += 10) {
        const batch = checkable.slice(i, i + 10);
        const out = await api<Record<string, VerifyResult>>("/api/v1/listings/ebay/verify", {
          method: "POST",
          body: JSON.stringify({ listings: batch, context: { ...profileIds, location } }),
        });
        Object.assign(results, out);
        setVerified({ ...results });
      }
    },
    onError: (e) => setVerifyError(e instanceof Error ? e.message : String(e)),
  });

  const acceptable = checkable.filter(
    (l) =>
      (verified[l.sku]?.ack === "Success" || verified[l.sku]?.ack === "Warning") &&
      (republish || !published[l.sku]),
  );
  const publish = useMutation({
    mutationFn: async (mode: "first" | "rest") => {
      if (!profileIds) throw new Error(t("import:needPolicyIds"));
      setPublishError(null);
      const todo = mode === "first" ? acceptable.slice(0, 1) : acceptable;
      const failed: string[] = [];
      for (let i = 0; i < todo.length; i += 10) {
        const out = await api<Record<string, PublishResult>>("/api/v1/listings/ebay/publish", {
          method: "POST",
          body: JSON.stringify({
            listings: todo.slice(i, i + 10),
            context: { ...profileIds, location },
            confirm: "PUBLISH",
            republish,
          }),
        });
        const ok: Record<string, string> = {};
        for (const [sku, r] of Object.entries(out)) {
          if (r.itemId) ok[sku] = r.itemId;
          else failed.push(`${sku}: ${r.errors[0]?.message ?? r.ack}`);
        }
        setPublished((p) => ({ ...p, ...ok }));
      }
      // Keep going past a failed listing; report them all once the rest are done.
      if (failed.length > 0) {
        throw new Error(`${failed.length} not published. ${failed.slice(0, 3).join(" | ")}`);
      }
    },
    onSettled: () => setConfirming(null),
    onError: (e) => setPublishError(e instanceof Error ? e.message : String(e)),
  });

  const onDownload = () => {
    if (!template) return;
    if (
      missingBlocking > 0 &&
      !window.confirm(t("import:confirmMissing", { count: missingBlocking }))
    )
      return;
    download(
      `ebay-upload-${new Date().toISOString().slice(0, 10)}.csv`,
      toEbayCsv(ready, rules, template),
    );
  };

  const fieldLabel = (k: keyof ColumnMap) => t(`import:field.${k}`);

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <h1 className="mb-1 font-semibold text-2xl tracking-tight">{t("import:title")}</h1>
      <p className="mb-5 text-muted-foreground text-sm">{t("import:hint")}</p>

      {error && (
        <Card className="mb-4 border-destructive/50">
          <CardContent className="p-4 text-sm">{error}</CardContent>
        </Card>
      )}

      <div className="mb-5 grid gap-3 md:grid-cols-3">
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="font-medium text-sm">1. {t("import:step.stock")}</div>
            <Input type="file" accept=".xlsx,.csv" onChange={(e) => onStock(e.target.files?.[0])} />
            {stock && (
              <div className="text-muted-foreground text-xs">
                {stock.name} · {merged?.items.length ?? 0} {t("import:rows")}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="font-medium text-sm">2. {t("import:step.template")}</div>
            <Input type="file" accept=".xlsx" onChange={(e) => onTemplate(e.target.files?.[0])} />
            {template && (
              <div className="text-muted-foreground text-xs">
                {template.headers.length} {t("import:columns")}
                {template.category ? ` · ${template.category.name} (${template.category.id})` : ""}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-2 p-4">
            <div className="font-medium text-sm">3. {t("import:step.supplement")}</div>
            <Input
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => onSupplement(e.target.files?.[0])}
            />
            <div className="text-muted-foreground text-xs">
              {supplement || savedRows.length > 1
                ? t("import:merged", {
                    matched: merged?.matched ?? 0,
                    unmatched: merged?.unmatched ?? 0,
                  })
                : t("import:supplementHint")}
            </div>
          </CardContent>
        </Card>
      </div>

      {stock && map && sheet && (
        <Card className="mb-5">
          <CardContent className="p-4">
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="font-medium text-sm">{t("import:mapping")}</div>
              {stock.sheets.length > 1 && (
                <select
                  className="rounded-md border bg-background px-2 py-1 text-sm"
                  value={sheetIdx}
                  onChange={(e) => {
                    const i = Number(e.target.value);
                    setSheetIdx(i);
                    const s = stock.sheets[i];
                    if (s) setMap(guessColumns(s.rows[findHeaderRow(s.rows)] ?? []));
                  }}
                >
                  {stock.sheets.map((s, i) => (
                    <option key={s.name} value={i}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {FIELD_KEYS.map((k) => (
                <label key={k} className="text-xs">
                  <span className="mb-1 block text-muted-foreground">{fieldLabel(k)}</span>
                  <select
                    className="w-full rounded-md border bg-background px-2 py-1 text-sm"
                    value={map[k]}
                    onChange={(e) => setMap({ ...map, [k]: Number(e.target.value) })}
                  >
                    <option value={-1}>—</option>
                    {headers.map((h, i) => (
                      <option key={`col-${String(i).padStart(3, "0")}`} value={i}>
                        {h || `(${i + 1})`}
                      </option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <p className="mt-2 text-muted-foreground text-xs">{t("import:qtyNote")}</p>
          </CardContent>
        </Card>
      )}

      {listings.length > 0 && (
        <>
          <Card className="mb-5">
            <CardContent className="grid gap-3 p-4 md:grid-cols-4">
              <div className="text-xs">
                <Label htmlFor="imp-brand" className="mb-1 block text-muted-foreground">
                  {t("import:rule.brand")}
                </Label>
                <Input
                  id="imp-brand"
                  value={rules.brand}
                  onChange={(e) => setRules({ ...rules, brand: e.target.value })}
                />
              </div>
              <div className="text-xs">
                <Label htmlFor="imp-pct" className="mb-1 block text-muted-foreground">
                  {t("import:rule.pricePct")}
                </Label>
                <Input
                  id="imp-pct"
                  type="number"
                  min={1}
                  max={200}
                  value={Math.round(rules.pricePct * 100)}
                  onChange={(e) => setRules({ ...rules, pricePct: Number(e.target.value) / 100 })}
                />
              </div>
              <div className="text-xs">
                <Label htmlFor="imp-minprice" className="mb-1 block text-muted-foreground">
                  {t("import:rule.minPrice")}
                </Label>
                <Input
                  id="imp-minprice"
                  type="number"
                  min={0}
                  value={rules.minListingPrice}
                  onChange={(e) => setRules({ ...rules, minListingPrice: Number(e.target.value) })}
                />
              </div>
              <div className="text-xs">
                <Label htmlFor="imp-packtarget" className="mb-1 block text-muted-foreground">
                  {t("import:rule.packTarget")}
                </Label>
                <Input
                  id="imp-packtarget"
                  type="number"
                  min={0}
                  value={rules.packTargetPrice}
                  onChange={(e) => setRules({ ...rules, packTargetPrice: Number(e.target.value) })}
                />
              </div>
              {(
                [
                  ["shippingProfile", "shipping"],
                  ["returnProfile", "returns"],
                  ["paymentProfile", "payment"],
                ] as const
              ).map(([key, listKey]) => (
                <div key={key} className="text-xs">
                  <Label htmlFor={`imp-${key}`} className="mb-1 block text-muted-foreground">
                    {t(`import:rule.${key}`)}
                  </Label>
                  {template && template.policies[listKey].length > 0 ? (
                    <select
                      id={`imp-${key}`}
                      className="w-full rounded-md border bg-background px-2 py-2 text-sm"
                      value={rules[key]}
                      onChange={(e) => setRules({ ...rules, [key]: e.target.value })}
                    >
                      {!template.policies[listKey].includes(rules[key]) && (
                        <option value={rules[key]}>{rules[key]}</option>
                      )}
                      {template.policies[listKey].map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={`imp-${key}`}
                      value={rules[key]}
                      onChange={(e) => setRules({ ...rules, [key]: e.target.value })}
                    />
                  )}
                  {policyWarn(rules[key], template?.policies[listKey]) && (
                    <span className="text-destructive">{t("import:policyMissing")}</span>
                  )}
                </div>
              ))}
              <div className="text-xs">
                <Label htmlFor="imp-location" className="mb-1 block text-muted-foreground">
                  {t("import:rule.location")}
                </Label>
                <Input
                  id="imp-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </div>
              <label className="flex items-center gap-2 self-end text-sm">
                <input
                  type="checkbox"
                  checked={rules.skipZeroQty}
                  onChange={(e) => setRules({ ...rules, skipZeroQty: e.target.checked })}
                />
                {t("import:rule.skipZero")}
              </label>
            </CardContent>
          </Card>

          <Card className="mb-5">
            <CardContent className="p-4">
              <div className="mb-2 font-medium text-sm">{t("import:categories")}</div>
              <div className="flex flex-wrap gap-3">
                {categories.map(([id, c]) => (
                  <div key={id} className="rounded-md border p-2 text-xs">
                    <Label htmlFor={`imp-cat-${id}`} className="mb-1 block">
                      {c.name} <span className="text-muted-foreground">({c.count})</span>
                    </Label>
                    <Input
                      id={`imp-cat-${id}`}
                      className="h-8 w-32"
                      placeholder={id}
                      value={categoryOverride[id] ?? ""}
                      onChange={(e) =>
                        setCategoryOverride({
                          ...categoryOverride,
                          [id]: e.target.value.replace(/\D/g, ""),
                        })
                      }
                    />
                    <div className="mt-1 text-muted-foreground">
                      {t("import:categoryId", { id })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="text-sm">
              <b>{ready.length}</b> {t("import:ready")} · {listings.length - ready.length}{" "}
              {t("import:skipped")}
            </div>
            {(Object.entries(flagCounts) as [Flag, number][]).map(([f, n]) => (
              <Badge
                key={f}
                variant={BLOCKING.includes(f) || f === "duplicateSku" ? "destructive" : "warning"}
              >
                {t(`import:flag.${f}`)} · {n}
              </Badge>
            ))}
            <label className="ml-auto flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyAttention}
                onChange={(e) => setOnlyAttention(e.target.checked)}
              />
              {t("import:onlyAttention")} ({listings.filter(needAttention).length})
            </label>
            <Button
              size="sm"
              variant="outline"
              disabled={!profileIds || checkable.length === 0 || verify.isPending}
              onClick={() => verify.mutate()}
            >
              {verify.isPending ? t("import:checking") : t("import:checkEbay")}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={acceptable.length === 0 || publish.isPending}
              onClick={() => setConfirming(Object.keys(published).length === 0 ? "first" : "rest")}
            >
              {t("import:publish")}
            </Button>
            <Button size="sm" disabled={!template || ready.length === 0} onClick={onDownload}>
              {t("import:download")}
            </Button>
          </div>
          {!template && (
            <p className="mb-3 text-muted-foreground text-xs">{t("import:needTemplate")}</p>
          )}
          {verifyError && <p className="mb-3 text-destructive text-sm">{verifyError}</p>}
          {confirming && (
            <Card className="mb-3 border-destructive">
              <CardContent className="space-y-3 p-4">
                <p className="font-medium text-sm">
                  {confirming === "first"
                    ? t("import:confirmFirst", { sku: acceptable[0]?.sku })
                    : t("import:confirmRest", { n: acceptable.length })}
                </p>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="republish"
                    checked={republish}
                    onCheckedChange={(v) => setRepublish(v === true)}
                  />
                  <Label htmlFor="republish" className="text-xs">
                    {t("import:republish")}
                  </Label>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={publish.isPending}
                    onClick={() => publish.mutate(confirming)}
                  >
                    {publish.isPending ? t("import:publishing") : t("import:confirmPublish")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirming(null)}>
                    {t("import:cancel")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {publishError && <p className="mb-3 text-destructive text-sm">{publishError}</p>}
          {Object.keys(published).length > 0 && (
            <p className="mb-3 text-sm">
              {t("import:publishedSummary", { n: Object.keys(published).length })}{" "}
              {Object.entries(published)
                .slice(0, 5)
                .map(([sku, id]) => (
                  <a
                    key={sku}
                    className="mr-2 underline"
                    href={`https://www.ebay.com/itm/${id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {sku}
                  </a>
                ))}
            </p>
          )}
          {Object.keys(verified).length > 0 && (
            <p className="mb-3 text-muted-foreground text-xs">
              {t("import:verifySummary", {
                ok: Object.values(verified).filter(
                  (v) => v.ack === "Success" || v.ack === "Warning",
                ).length,
                bad: Object.values(verified).filter(
                  (v) => v.ack !== "Success" && v.ack !== "Warning",
                ).length,
              })}
            </p>
          )}

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground text-xs">
                    <th className="p-3">SKU</th>
                    <th className="p-3">{t("import:col.title")}</th>
                    <th className="p-3">{t("import:col.category")}</th>
                    <th className="p-3 text-right">{t("import:col.qty")}</th>
                    <th className="p-3 text-right">{t("import:col.price")}</th>
                    <th className="p-3">{t("import:col.flags")}</th>
                    <th className="p-3">eBay</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((l, i) => (
                    <tr
                      key={`${l.sku}#${String(i).padStart(4, "0")}`}
                      className={l.blocked ? "border-b opacity-50" : "border-b"}
                    >
                      <td className="p-3 font-mono text-xs">
                        {l.sku}
                        {l.pack > 1 && (
                          <Badge variant="info" className="ml-2">
                            {t("import:lot", { n: l.pack })}
                          </Badge>
                        )}
                      </td>
                      <td className="max-w-md p-3">
                        <div>{l.title}</div>
                        {l.title !== l.originalTitle && (
                          <div
                            className="truncate text-muted-foreground text-xs"
                            title={l.originalTitle}
                          >
                            {t("import:was")} {l.originalTitle}
                          </div>
                        )}
                        <div className="text-muted-foreground text-xs">{l.title.length}/80</div>
                      </td>
                      <td className="p-3 text-xs">
                        {l.category.name}{" "}
                        <span className="text-muted-foreground">{l.category.id}</span>
                      </td>
                      <td className="p-3 text-right tabular-nums">{l.qty}</td>
                      <td className="p-3 text-right tabular-nums">
                        {l.price === null ? "—" : l.price.toFixed(2)}
                      </td>
                      <td className="space-x-1 p-3">
                        {l.flags.map((f) => (
                          <Badge
                            key={f}
                            variant={
                              BLOCKING.includes(f) || f === "duplicateSku"
                                ? "destructive"
                                : "warning"
                            }
                          >
                            {t(`import:flag.${f}`)}
                          </Badge>
                        ))}
                      </td>
                      <td className="max-w-xs p-3 text-xs">
                        {(() => {
                          const v = verified[l.sku];
                          if (!v) return null;
                          const bad = v.ack !== "Success" && v.ack !== "Warning";
                          return (
                            <div>
                              <Badge
                                variant={
                                  bad ? "destructive" : v.errors.length ? "warning" : "success"
                                }
                              >
                                {bad
                                  ? t("import:ebay.rejected")
                                  : v.errors.length
                                    ? t("import:ebay.warnings")
                                    : t("import:ebay.ok")}
                              </Badge>
                              {v.errors.map((e) => (
                                <div
                                  key={`${e.code}-${e.message.slice(0, 20)}`}
                                  className="mt-1 text-muted-foreground"
                                >
                                  {e.message}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
